import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  SvgIcon,
  useTheme,
  Stack,
  Tooltip,
  alpha,
} from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { getRegularDate } from "utils/time.util";
import {
  setCollectionId,
  setCollectionSourceId,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSummaryInfoData,
} from "context/features/dataSlice";

import { ReactComponent as summarizerLogo } from "assets/icons/summarizer-icon.svg";
import { ReactComponent as citationLogo } from "assets/icons/citation-generator-icon.svg";
import { ReactComponent as readingLogo } from "assets/icons/reading-level-icon.svg";

import { useDispatch, useSelector } from "react-redux";
import { writeStorage, useLocalStorage } from "@rehooks/local-storage";
import NewsguardScore from "components/base/NewsguardScore";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import { GppMaybeOutlined } from "@mui/icons-material";
import ColumnsSection from "./ColumnSection";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import { useColumns } from "features/SourceSearch/components/generate-columns";
import CollectionTableComponent from "features/SourceSearch/components/collection-table-component";
import {
  singleCollectionColumns,
  singleCollectiondefaultVisibilityModel,
  teacherSubmissionDefaultVisibilityModel,
} from "../data/columns";
import { isDatabaseSourceUrl } from "utils/database-source.util";

export default function RecentDataGrid({
  recentCollectionSource,
  loading = false,
  filter,
  teacherMode = false,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const { user } = useSelector((state) => state.auth);

  const noRowsMessage = filter
    ? "No bookmarks found matching your search."
    : teacherMode
      ? "This student hasn't added any bookmarks to this collection."
      : "It looks like you don't have any bookmarks yet!";

  const defaultModel = teacherMode
    ? teacherSubmissionDefaultVisibilityModel
    : singleCollectiondefaultVisibilityModel;

  // Bumped so any previously-persisted "show Collections column" preference
  // (from before collectionMemberships defaulted to hidden here) gets discarded.
  const storageKey = teacherMode
    ? `sooth-api-columnvisibility-teacher-submission-v2-${user.userId}`
    : `sooth-api-columnvisibility-v4-${user.userId}`;

  const [storedVisibilityModel, setVisibilityModel] = useLocalStorage(
    storageKey,
    defaultModel
  );

  const visibilityModel = {
    ...defaultModel,
    ...storedVisibilityModel,
  };

  const [persistedWidths, setPersistedWidths] = useLocalStorage(
    `sooth-api-columnWidths-${user.userId}`,
    {}
  );

  const [commonWidth, setCommonWidth] = useState(null);

  const columnWidthsRef = useRef(persistedWidths);
  const containerRef = useRef(null);

  const computeCommonWidth = () => {
    const element = containerRef.current;
    let width = element ? element.clientWidth : 0;

    const visibleFields = Object.keys(visibilityModel).filter(
      (field) => visibilityModel[field]
    );
    const unsetCount = visibleFields.length;
    const newCommonWidth = unsetCount > 0 ? Math.floor(width / unsetCount) : 0;
    setCommonWidth(newCommonWidth);
  };

  const debouncedUpdateColumnWidths = () => {
    writeStorage(`sooth-api-columnWidths-${user.userId}`, columnWidthsRef.current);
    setPersistedWidths(columnWidthsRef.current);
    computeCommonWidth();
  };

  const handleColumnResize = (params) => {
    columnWidthsRef.current = {
      ...columnWidthsRef.current,
      [params.colDef.field]: params.width,
    };
    debouncedUpdateColumnWidths();
  };

  useEffect(() => {
    if (!visibilityModel || Object.keys(visibilityModel).length === 0) {
      setVisibilityModel(defaultModel);
      writeStorage(storageKey, defaultModel);
      apiRef.current.setColumnVisibilityModel(defaultModel);
    } else {
      apiRef.current.setColumnVisibilityModel(visibilityModel);
    }
  }, []);

  const handleVisibilityChange = (newVisibilityModel) => {
    const updatedVisibility = {
      ...visibilityModel,
      ...newVisibilityModel,
    };
    setVisibilityModel(updatedVisibility);
    writeStorage(storageKey, updatedVisibility);
  };

  const handleToggleColumnVisibility = (event, field) => {
    const newVisibilityModel = {
      ...visibilityModel,
      [field]: event.target.checked,
    };
    setVisibilityModel(newVisibilityModel);
    writeStorage(storageKey, newVisibilityModel);
  };

  const handleSidebarOpen = (item, option) => {
    const isDatabaseSearch = isDatabaseSourceUrl(item.sourceUrl_chr);
    dispatch(setCollectionSourceId(item.collectionSourceID_ids));
    dispatch(setLevel(""));
    dispatch(
      setSelectedBookmarkOption(
        isDatabaseSearch && option === "readinglevel" ? "notes" : option
      )
    );
    dispatch(setReadingLevelAvailable(item.isReadingLevelAvailable_ysn));
    dispatch(
      setSummaryInfoData({
        publisher: item.sourceName_chr,
        title: item.title_chr,
        published_date: item.sourcePublishedDateTime_dtm,
        url: item.sourceUrl_chr,
        utc_date_published: item.sourcePublishedDateTime_dtm,
        isDatabaseSearch,
      })
    );
    dispatch(setCollectionId(item.collectionID_lng));
    dispatch(setIsCitationAvailable(item.isCitationAvailable_ysn));
    dispatch(setIsSummaryAvailable(item.isSummaryAvailable_ysn));
    dispatch(setIsSidebarOpen(true));
  };

  const columns = useColumns({
    fields: singleCollectionColumns,
    persistedWidths,
    commonWidth,
    theme,
    handlers: { handleSidebarOpen },
  });

  const scrollToLeft = () => {
    const virtualScroller = document.querySelector(
      ".MuiDataGrid-virtualScroller"
    );
    if (virtualScroller) {
      virtualScroller.scrollLeft = 0;
    }
  };
  const clearWidthModel = () => {
    scrollToLeft();
    writeStorage(`sooth-api-columnWidths-${user.userId}`, {});
    setCommonWidth(null);
    setPersistedWidths({});
    columnWidthsRef.current = {};
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <ColumnsSection
        visibilityModel={visibilityModel}
        setVisibilityModel={setVisibilityModel}
        columns={columns}
        label={storageKey}
        clearWidthModel={clearWidthModel}
        widthModal={persistedWidths}
      />
      <CollectionTableComponent
        apiRef={apiRef}
        rows={recentCollectionSource}
        columns={columns}
        handleColumnResize={handleColumnResize}
        visibilityModel={visibilityModel}
        handleVisibilityChange={handleVisibilityChange}
        getRowId={(row) => row.collectionSourceID_ids}
        getRowClassName={({ row }) =>
          row.isMisinformationSource_ysn ? "sooth-misinformation-row" : ""
        }
        showCustomEmptyMessage={true}
        loading={loading}
        noRowsMessage={noRowsMessage}
      />
    </Box>
  );
}
